import type { TabItemProps } from "#src/store/tabs";
import type { TabsProps } from "antd";

import { RedoOutlined } from "@ant-design/icons";
import { useCurrentRoute } from "#src/hooks/use-current-route";
import {usePreferences} from "#src/hooks/use-preferences";
import { removeTrailingSlash } from "#src/router/utils/remove-trailing-slash";
import { useAccessStore } from "#src/store/access";
import { usePreferencesStore } from "#src/store/preferences";
import { useTabsStore } from "#src/store/tabs";
import {cn} from "#src/utils/cn";
import { isString } from "#src/utils/is";
import { Button, Tabs } from "antd";
import { clsx } from "clsx";
import { isValidElement, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { tabbarHeight } from "../constants";
import { DraggableTabBar } from "./components/draggable-tab-bar";
import { TabOptions } from "./components/tab-options";
import { TabActionKeys, useDropdownMenu } from "./hooks/use-dropdown-menu";
import { useStyles } from "./style";

export interface LayoutTabbarProps {
	computedSidebarWidth: number
}
/**
 * LayoutTabbar Component
 * Used to render and manage the application's tab navigation
 */
export default function LayoutTabbar({ computedSidebarWidth }: LayoutTabbarProps) {
	// const { token } = theme.useToken();
	const classes = useStyles();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const currentRoute = useCurrentRoute();

	const { tabbarStyleType, tabbarShowMaximize, tabbarShowMore } = usePreferencesStore();
	const { flatRouteList } = useAccessStore();
	const { activeKey, isRefresh, setActiveKey, setIsRefresh, openTabs, addTab, insertBeforeTab } = useTabsStore();
	const [items, onClickMenu] = useDropdownMenu();
	const {isDark} = usePreferences();

	const tabItems: TabItemProps[] = Array.from(openTabs.values()).map((item) => {
		const tabLabel = item.newTabTitle ?? item.label;
		return {
			...item,
			label: (
				<div className={cn("relative flex items-center gap-1", isDark ? "text-colorText" : "")}>
					{isString(tabLabel) ? t(tabLabel) : tabLabel}
				</div>
			),
		};
	});

	/**
	 * Automatically reset the refresh state
	 */
	useEffect(() => {
		if (isRefresh) {
			const timer = setTimeout(() => {
				setIsRefresh(false);
			}, 500);

			return () => clearTimeout(timer);
		}
	}, [isRefresh, setIsRefresh]);

	/**
	 * Handle tab switching
	 * @param {string} key - The key of the selected tab
	 */
	const handleChangeTabs = useCallback((key: string) => {
		const historyState = openTabs.get(key)?.historyState || { search: "", hash: "" };
		navigate(key + historyState.search + historyState.hash);
	}, [openTabs]);

	/**
	 * Handle tab editing (closing)
	 * @param {React.MouseEvent | React.KeyboardEvent | string} key - The key of the tab being edited
	 * @param {string} action - The editing action, only handling "remove" here
	 */
	const handleEditTabs = useCallback<Required<TabsProps>["onEdit"]>((key, action) => {
		if (action === "remove") {
			onClickMenu(TabActionKeys.CLOSE, key as string);
		}
	}, [onClickMenu]);

	/**
	 * Custom tab bar rendering, adding right-click menu functionality
	 * @param {object} tabBarProps - Tab bar properties
	 * @param {React.ComponentType} DefaultTabBar - Default tab bar component
	 * @returns {JSX.Element} The rendered tab bar
	 */
	const renderTabBar = useCallback<Required<TabsProps>["renderTabBar"]>((tabBarProps, DefaultTabBar) => {
		return (
			<DraggableTabBar
				DefaultTabBar={DefaultTabBar}
				tabBarProps={tabBarProps}
				items={items}
				tabItems={tabItems}
				onClickMenu={onClickMenu}
			/>
		);
	}, [tabItems, items, onClickMenu]);

	/**
	 * Generate tab bar extra content
	 */
	const tabBarExtraContent = useMemo(() => ({
		right: (
			<div className="flex items-center" style={{ height: tabbarHeight }}>
				<Button
					icon={(
						<RedoOutlined
							rotate={270}
							className={clsx({ "animate-spin": isRefresh })}
						/>
					)}
					size="middle"
					type="text"
					className={clsx("rounded-none h-full border-l border-l-colorBorderSecondary")}
					onClick={() => onClickMenu(TabActionKeys.REFRESH, activeKey)}
				/>
				{/* {tabbarShowMaximize ? (<TabMaximize className="h-full border-l rounded-none border-l-colorBorderSecondary" />) : null} */}
				{tabbarShowMore ? (<TabOptions activeKey={activeKey} className="h-full border-l rounded-none border-l-colorBorderSecondary" />) : null}
			</div>
		),
	}), [isRefresh, activeKey, onClickMenu, tabbarShowMore, tabbarShowMaximize]);

	/**
	 * Active tab is closed, automatically navigate to the appropriate route
	 *
	 * Warning: Except for the initial entry into the system (e.g., login), please uniformly use navigate(import.meta.env.VITE_BASE_HOME_PATH) instead of directly using navigate("/") in the project, for the following reasons:
	 * 1. Directly navigating to the root path ("/") will cause the route root component to re-render
	 * 2. This component will not be able to correctly monitor location changes
	 * 3. It will cause the activeKey state to remain the previous active tab (display anomaly)
	 * 4. The result is that location.pathname is new, but activeKey state is the previous active tab, leading to navigation anomaly.
	 */
	useEffect(() => {
		/**
		 * The following actions will trigger the active tab to be closed:
		 * 1. Closing the current tab
		 * 2. When using the Close Left/Right/Other/All tabs function, the active tab is closed
		 *
		 * At this point, activeKey is the latest, location.pathname has not been updated yet. Use navigate to navigate to the latest active tab to prevent display anomalies.
		 *
		 * When entering the application for the first time, activeKey is empty, so automatic navigation is not triggered
		 */
		const historyState = openTabs.get(activeKey)?.historyState || { search: "", hash: "" };
		const activeFullPath = activeKey + historyState.search + historyState.hash;
		const currentFullpath = location.pathname + location.search + location.hash;
		if (activeKey.length > 0 && activeFullPath !== currentFullpath) {
			navigate(activeFullPath);
		}
	}, [activeKey]);

	/**
	 * When the user refreshes the current page, but it is not the default Tab page, the default Tab needs to be added
	 */
	useEffect(() => {
		// Check if the default Tab is missing
		const isDefaultTabMissing = !Array.from(openTabs.keys()).includes(import.meta.env.VITE_BASE_HOME_PATH);

		if (isDefaultTabMissing) {
			const routeTitle = flatRouteList[import.meta.env.VITE_BASE_HOME_PATH]?.handle?.title;
			insertBeforeTab(import.meta.env.VITE_BASE_HOME_PATH, {
				key: import.meta.env.VITE_BASE_HOME_PATH,
				label: isValidElement(routeTitle) ? routeTitle?.props?.children : routeTitle,
				closable: false,
				draggable: false,
			});
		}
	}, [openTabs, insertBeforeTab, flatRouteList]);

	/**
	 * Listen for route changes, add tabs, and activate tabs
	 */
	useEffect(() => {
		const activePath = location.pathname;
		const normalizedPath = removeTrailingSlash(activePath);
		// The tabbarEnable variable may cause this component to mount and unmount. normalizedPath may be the same as activeKey, so a check is added to prevent repeated addTab calls
		if (normalizedPath !== activeKey) {
			setActiveKey(normalizedPath);

			const routeTitle = currentRoute.handle?.title;

			addTab(normalizedPath, {
				key: normalizedPath,
				// Ensure the label is of string type to be stored in sessionStorage.
				label: isValidElement(routeTitle) ? routeTitle?.props?.children : routeTitle,
				historyState: { search: location.search, hash: location.hash },
				/* The default route to jump to after login, cannot be closed or dragged */
				closable: normalizedPath !== import.meta.env.VITE_BASE_HOME_PATH,
				draggable: normalizedPath !== import.meta.env.VITE_BASE_HOME_PATH,
			});
		}
	}, [location, currentRoute, setActiveKey, addTab]);

	return (
		<div className={classes.tabsContainer} style={{ paddingLeft: computedSidebarWidth }}>
			<Tabs
				className={clsx(
					classes.resetTabs,
					tabbarStyleType === "brisk" ? classes.brisk : "",
					tabbarStyleType === "plain" ? classes.plain : "",
					tabbarStyleType === "chrome" ? classes.chrome : "",
					tabbarStyleType === "card" ? classes.card : "",
				)}
				size="small"
				hideAdd
				animated
				onChange={handleChangeTabs}
				activeKey={removeTrailingSlash(activeKey)}
				type="editable-card"
				onEdit={handleEditTabs}
				items={tabItems}
				renderTabBar={renderTabBar}
				tabBarExtraContent={tabBarExtraContent}
			/>
		</div>
	);
}
